/* eslint-disable no-unused-vars */
import { Box, IconButton, useTheme, Typography, Button, Menu, Divider, Avatar, Tooltip, Modal, Chip } from "@mui/material";
import { useContext, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SettingsMenu from "../../components/SettingsMenu";
import SettingsChips from "../../components/SettingsChips";
import { useSettings } from "../../contexts/SettingsContext";
import { SignedIn, SignedOut, useUser, SignOutButton } from "@clerk/clerk-react";
import AuthButtons from "../../components/AuthButtons";
import SleeperConnect from "../../components/SleeperConnect";
import { useSleeperConnection } from "../../hooks/useSleeperConnection";
import { useSleeperModal } from "../../contexts/SleeperModalContext";


const Topbar = ({ 
    // sidebarHidden,
    // setSidebarHidden,
    isDraftboardPage,
    onOpenSidebar,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const colorMode = useContext(ColorModeContext);
    const settings = useSettings();
    const { isOpen: showSleeperModal, openModal: setShowSleeperModal, closeModal } = useSleeperModal();
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
    const [userAnchorEl, setUserAnchorEl] = useState(null);

    const handleSettingsClick = (event) => {
        setSettingsAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsAnchorEl(null);
    };

    // const toggleSidebarVisibility = () => {
    //     setSidebarHidden(!sidebarHidden);
    // };

    const handleUserClick = (event) => {
        setUserAnchorEl(event.currentTarget);
    };

    const handleUserClose = () => {
        setUserAnchorEl(null);
    };

    const isConnected = Boolean(settings.draftId);
    const handleConnectToggle = () => {
        if (isConnected) {
            const confirmDisconnect = window.confirm("Disconnect from the current draft?");
            if (confirmDisconnect) {
                settings.handleDraftIdChange("");
            }
            return;
        }
        // Connect flow: allow choosing recent or entering new
        const recent = settings.recentDraftIds || [];
        if (recent.length > 0) {
            const recentOptions = recent.map((id, i) => `${i + 1}. ${id}`).join("\n");
            const input = window.prompt(`Recent Draft IDs:\n${recentOptions}\n\nEnter a number (1-${recent.length}) to choose, or paste a new Draft ID:`);
            if (!input) return;
            const n = parseInt(input, 10);
            if (!Number.isNaN(n) && n >= 1 && n <= recent.length) {
                settings.handleDraftIdChange(recent[n - 1]);
            } else {
                settings.handleDraftIdChange(input);
            }
            return;
        }
        const id = window.prompt("Enter or paste your Sleeper Draft ID:");
        if (id) settings.handleDraftIdChange(id);
    };

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} gap={2}>
            {/* LEFT SECTION: Mobile hamburger (XS/SM) */}
            <Box display="flex" alignItems="center" gap={2} flex={1}>
                <IconButton
                    onClick={onOpenSidebar}
                    sx={{ display: { xs: 'inline-flex', sm: 'inline-flex', md: 'none' } }}
                    aria-label="Open navigation"
                >
                    <MenuOutlinedIcon />
                </IconButton>
            </Box>

            {/* RIGHT SECTION: Theme + Settings + User Controls */}
            <Box display="flex" alignItems="center" gap={1}>
                {isDraftboardPage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, gap: 1 }}>
                        {/* IconButton on XS/SM */}
                        <Tooltip title={isConnected ? "Disconnect draft" : "Connect to draft"}>
                            <IconButton
                                onClick={handleConnectToggle}
                                sx={{
                                    display: { xs: 'inline-flex', sm: 'inline-flex', md: 'none' },
                                    p: 1,
                                    "& .MuiSvgIcon-root": { fontSize: '1.25rem' }
                                }}
                            >
                                {isConnected ? (
                                    <LinkOffOutlinedIcon />
                                ) : (
                                    <LinkOutlinedIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                        {/* Text Button on MD+ */}
                        <Button
                            onClick={handleConnectToggle}
                            variant="contained"
                            size="small"
                            sx={{
                                display: { xs: 'none', sm: 'none', md: 'inline-flex' },
                                textTransform: 'uppercase',
                                fontWeight: 800,
                                fontSize: '11px',
                                px: 1.5,
                                py: 0.5,
                                minHeight: '28px',
                                borderRadius: '6px',
                                bgcolor: isConnected ? colors.redAccent[500] : colors.greenAccent[600],
                                '&:hover': {
                                    bgcolor: isConnected ? colors.redAccent[600] : colors.greenAccent[500],
                                },
                            }}
                        >
                            {isConnected ? 'Disconnect' : 'Connect Draft'}
                        </Button>
                        <SettingsChips
                            scoringType={settings.scoringType}
                            platform={settings.platform}
                            draftId={settings.draftId}
                        />
                    </Box>
                )}
                <IconButton onClick={colorMode.toggleColorMode}>
                    {theme.palette.mode === "dark" ? (
                        <DarkModeOutlinedIcon />
                    ) : (
                        <LightModeOutlinedIcon />
                    )}
                </IconButton>
                {/* <IconButton>
                    <NotificationsOutlinedIcon />
                </IconButton>  */}
                <IconButton onClick={handleSettingsClick}>
                    <SettingsOutlinedIcon />
                </IconButton>
                <IconButton onClick={handleUserClick}>
                    <PersonOutlinedIcon />
                </IconButton>
            </Box>
            <SettingsMenu 
                anchorEl={settingsAnchorEl}
                open={Boolean(settingsAnchorEl)}
                onClose={handleSettingsClose}
            />
            <Menu
                anchorEl={userAnchorEl}
                open={Boolean(userAnchorEl)}
                onClose={handleUserClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        px: 2,
                        py: 1.5,
                        backgroundColor: colors.primary[400],
                        border: `1px solid ${colors.grey[700]}`,
                        minWidth: 260,
                    },
                }}
            >
                <SignedIn>
                    <UserMenuContent 
                        colors={colors} 
                        onClose={handleUserClose}
                        onOpenSleeperModal={() => {
                            setShowSleeperModal(true);
                            setUserAnchorEl(null);
                        }}
                    />
                </SignedIn>
                <SignedOut>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Typography sx={{ color: colors.grey[100], fontWeight: 700 }}>Welcome</Typography>
                        <AuthButtons compact />
                    </Box>
                </SignedOut>
            </Menu>
            
            {/* SleeperConnect Modal */}
            <Modal
                open={showSleeperModal}
                onClose={closeModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Box>
                    <SleeperConnect onClose={closeModal} />
                </Box>
            </Modal>
        </Box>
    );
};

export default Topbar;

function UserMenuContent({ colors, onClose, onOpenSleeperModal }) {
    const { user } = useUser();
    const { isConnected, leagues, disconnect } = useSleeperConnection();
    const { openModal } = useSleeperModal();
    
    const fullName = user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    const email = user?.primaryEmailAddress?.emailAddress || "";
    const avatarUrl = user?.imageUrl;

    const handleSleeperConnect = () => {
        openModal();
        onClose();
    };

    const handleSleeperDisconnect = () => {
        const confirmDisconnect = window.confirm("Disconnect from Sleeper? This will clear your imported leagues.");
        if (confirmDisconnect) {
            disconnect();
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar src={avatarUrl} alt={fullName} sx={{ width: 40, height: 40 }} />
                <Box>
                    <Typography sx={{ color: colors.grey[100], fontWeight: 700 }}>
                        {fullName || "User"}
                    </Typography>
                    {email && (
                        <Typography sx={{ color: colors.grey[300], fontSize: 12 }}>{email}</Typography>
                    )}
                </Box>
            </Box>
            <Divider sx={{ borderColor: colors.grey[700] }} />
            
            {/* Sleeper Connection Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {isConnected ? (
                    <>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography sx={{ color: colors.grey[100], fontSize: 14, fontWeight: 600 }}>
                                Sleeper Connected
                            </Typography>
                            <Chip
                                label={`${leagues.length} ${leagues.length === 1 ? 'League' : 'Leagues'}`}
                                size="small"
                                sx={{
                                    backgroundColor: colors.greenAccent[600],
                                    color: colors.grey[100],
                                    fontSize: 11,
                                }}
                            />
                        </Box>
                        <Button
                            onClick={handleSleeperDisconnect}
                            size="small"
                            sx={{
                                color: colors.redAccent[400],
                                textTransform: "none",
                                justifyContent: "flex-start",
                                px: 0,
                            }}
                        >
                            Disconnect from Sleeper
                        </Button>
                    </>
                ) : (
                    <Button
                        onClick={handleSleeperConnect}
                        variant="outlined"
                        size="small"
                        sx={{
                            borderColor: colors.greenAccent[500],
                            color: colors.greenAccent[500],
                            textTransform: "uppercase",
                            fontWeight: 600,
                            "&:hover": {
                                borderColor: colors.greenAccent[400],
                                backgroundColor: "rgba(76,206,172,0.08)",
                            },
                        }}
                    >
                        Connect to Sleeper
                    </Button>
                )}
            </Box>
            
            <Divider sx={{ borderColor: colors.grey[700] }} />
            <SignOutButton>
                <Button
                    color="secondary"
                    variant="contained"
                    onClick={onClose}
                    sx={{ fontWeight: 700, textTransform: "uppercase" }}
                >
                    Sign Out
                </Button>
            </SignOutButton>
        </Box>
    );
}